import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Chip,
  Button,
  IconButton,
  Link,
  Dialog,
  DialogContent,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Card,
  CardContent,
} from '@mui/material';
import {
  LocationOn,
  Bed,
  Bathroom,
  SquareFoot,
  Garage,
  Pool,
  Fireplace,
  Balcony,
  Favorite,
  Share,
  Edit,
  Delete,
  ArrowBack,
  ZoomIn,
  Close as CloseIcon,
  NavigateNext,
  NavigateBefore,
  Map as MapIcon,
  Description,
  PlayCircle,
  ThreeDRotation,
  ViewInAr,
  Visibility,
} from '@mui/icons-material';
import { Property } from '@/types';
import { format } from 'date-fns';
import { PropertyService } from '@/services/propertyService';

interface PropertyDetailProps {
  property: Property;
  matchScore?: number;
  onEdit?: (property: Property) => void;
  onDelete?: (property: Property) => void;
  onBack?: () => void;
  showActions?: boolean;
}

const PropertyDetail: React.FC<PropertyDetailProps> = ({
  property,
  matchScore,
  onEdit,
  onDelete,
  showActions = true,
  onBack,
}) => {
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [viewCount, setViewCount] = useState(property.analytics.views);

  useEffect(() => {
    // Track property view
    const trackView = async () => {
      try {
        await PropertyService.incrementPropertyViews(property.id);
        setViewCount((prev) => prev + 1);
      } catch (error) {
        console.error('Failed to track view:', error);
      }
    };

    trackView();
  }, [property.id]);

  const primaryImage = property.metadata.images?.[0] || '/placeholder-property.jpg';
  const images = property.metadata.images || [primaryImage];
  const price = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: property.metadata.currency || 'USD',
    minimumFractionDigits: 0,
  }).format(property.metadata.price);

  const handleImageClick = (index: number) => {
    setSelectedImageIndex(index);
    setLightboxOpen(true);
  };

  const handleNextImage = () => {
    setSelectedImageIndex((prev) => (prev + 1) % images.length);
  };

  const handlePrevImage = () => {
    setSelectedImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: property.metadata.title,
          text: property.metadata.description,
          url: window.location.href,
        });
      } catch (error) {
        console.error('Error sharing:', error);
      }
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(window.location.href);
      alert('Link copied to clipboard!');
    }
  };

  const [calculatorOpen, setCalculatorOpen] = useState(false);

  const registrationDuty = property.metadata.price * 0.04;
  const landRegistryFee = property.metadata.price * 0.015;
  const notaryFee = property.metadata.price * 0.01;
  const totalClosingCosts = registrationDuty + landRegistryFee + notaryFee;
  const totalCost = property.metadata.price + totalClosingCosts;

  const formatCurrency = (amount: number) => new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: property.metadata.currency || 'USD',
    minimumFractionDigits: 0,
  }).format(amount);

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box sx={{ flex: 1 }}>
          {onBack && (
            <IconButton onClick={onBack} sx={{ mb: 2 }}>
              <ArrowBack />
            </IconButton>
          )}
          <Typography variant="h4" gutterBottom>
            {property.metadata.title}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
            <Chip
              label={property.metadata.propertyType}
              color="primary"
              size="small"
            />
            <Chip
              label={property.metadata.listingType}
              color="secondary"
              size="small"
            />
            {property.isFeatured && (
              <Chip label="Featured" color="warning" size="small" />
            )}
            {!property.isActive && (
              <Chip label="Inactive" size="small" />
            )}
            {matchScore !== undefined && (
              <Chip 
                label={`${matchScore}% Match`} 
                color={matchScore >= 80 ? "success" : matchScore >= 60 ? "warning" : "default"}
                size="small"
                sx={{ fontWeight: 'bold' }}
              />
            )}
          </Box>
        </Box>
        {showActions && (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <IconButton onClick={handleShare} color="primary">
              <Share />
            </IconButton>
            <IconButton color="primary">
              <Favorite />
            </IconButton>
            {onEdit && (
              <Button
                variant="outlined"
                startIcon={<Edit />}
                onClick={() => onEdit(property)}
              >
                Edit
              </Button>
            )}
            {onDelete && (
              <Button
                variant="outlined"
                color="error"
                startIcon={<Delete />}
                onClick={() => onDelete(property)}
              >
                Delete
              </Button>
            )}
          </Box>
        )}
      </Box>

      <Grid container spacing={3}>
        {/* Image Gallery */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ overflow: 'hidden' }}>
            <Box
              sx={{
                position: 'relative',
                width: '100%',
                height: 500,
                bgcolor: 'grey.200',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
              }}
              onClick={() => handleImageClick(selectedImageIndex)}
            >
              <img
                src={images[selectedImageIndex]}
                alt={property.metadata.title}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                }}
              />
              {images.length > 1 && (
                <>
                  <IconButton
                    sx={{
                      position: 'absolute',
                      left: 8,
                      bgcolor: 'rgba(255,255,255,0.8)',
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handlePrevImage();
                    }}
                  >
                    <NavigateBefore />
                  </IconButton>
                  <IconButton
                    sx={{
                      position: 'absolute',
                      right: 8,
                      bgcolor: 'rgba(255,255,255,0.8)',
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleNextImage();
                    }}
                  >
                    <NavigateNext />
                  </IconButton>
                </>
              )}
              <IconButton
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  bgcolor: 'rgba(255,255,255,0.8)',
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleImageClick(selectedImageIndex);
                }}
              >
                <ZoomIn />
              </IconButton>
            </Box>

            {/* Thumbnail Gallery */}
            {images.length > 1 && (
              <Box
                sx={{
                  display: 'flex',
                  gap: 1,
                  p: 2,
                  overflowX: 'auto',
                }}
              >
                {images.map((image, index) => (
                  <Box
                    key={index}
                    onClick={() => setSelectedImageIndex(index)}
                    sx={{
                      minWidth: 100,
                      height: 100,
                      cursor: 'pointer',
                      border: selectedImageIndex === index ? 3 : 1,
                      borderColor: selectedImageIndex === index ? 'primary.main' : 'divider',
                      borderRadius: 1,
                      overflow: 'hidden',
                    }}
                  >
                    <img
                      src={image}
                      alt={`Thumbnail ${index + 1}`}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                      }}
                    />
                  </Box>
                ))}
              </Box>
            )}
          </Paper>

          {/* Map Section */}
          <Paper sx={{ p: 3, mt: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  Location
                </Typography>
                <Button 
                  variant="outlined" 
                  startIcon={<MapIcon />}
                  size="small"
                  onClick={() => window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${property.location.address}, ${property.location.city}, ${property.location.state}`)}`, '_blank')}
                >
                  Get Directions
                </Button>
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <LocationOn color="primary" sx={{ mr: 1 }} />
              <Typography>
                {property.location.address}, {property.location.city}, {property.location.state} {property.location.zipCode}
              </Typography>
            </Box>
            
            {/* Map placeholder - would integrate with Google Maps API */}
            <Box
              sx={{
                width: '100%',
                height: 300,
                bgcolor: 'grey.200',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: 1,
                flexDirection: 'column',
                gap: 2
              }}
            >
              <LocationOn sx={{ fontSize: 60, color: 'text.secondary' }} />
              <Typography color="text.secondary" align="center">
                Map View
                <br />
                <Typography variant="caption">
                    Lat: {property.location.latitude}, Lng: {property.location.longitude}
                </Typography>
              </Typography>
            </Box>
            {property.location.neighborhood && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Neighborhood: {property.location.neighborhood}
              </Typography>
            )}
            {property.location.schoolDistrict && (
              <Typography variant="body2" color="text.secondary">
                School District: {property.location.schoolDistrict}
              </Typography>
            )}
          </Paper>
        </Grid>

        {/* Sidebar */}
        <Grid item xs={12} md={4}>
          {/* Price Card */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h4" color="primary" fontWeight="bold" gutterBottom>
                {price}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {property.metadata.listingType.charAt(0).toUpperCase() + property.metadata.listingType.slice(1)}
              </Typography>
              <Divider sx={{ my: 2 }} />
              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <Bed />
                  </ListItemIcon>
                  <ListItemText primary={`${property.metadata.bedrooms} Bedrooms`} />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <Bathroom />
                  </ListItemIcon>
                  <ListItemText primary={`${property.metadata.bathrooms} Bathrooms`} />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <SquareFoot />
                  </ListItemIcon>
                  <ListItemText
                    primary={`${property.metadata.squareFootage.toLocaleString()} sq ft`}
                  />
                </ListItem>
                {property.metadata.lotSize && (
                  <ListItem>
                    <ListItemIcon>
                      <SquareFoot />
                    </ListItemIcon>
                    <ListItemText
                      primary={`Lot: ${property.metadata.lotSize.toLocaleString()} sq ft`}
                    />
                  </ListItem>
                )}
                {property.metadata.yearBuilt && (
                  <ListItem>
                    <ListItemText primary={`Year Built: ${property.metadata.yearBuilt}`} />
                  </ListItem>
                )}
                {property.metadata.parkingSpaces && (
                  <ListItem>
                    <ListItemIcon>
                      <Garage />
                    </ListItemIcon>
                    <ListItemText primary={`${property.metadata.parkingSpaces} Parking Spaces`} />
                  </ListItem>
                )}
              </List>
              
              <Button
                variant="outlined"
                color="primary"
                fullWidth
                sx={{ mt: 2 }}
                onClick={() => setCalculatorOpen(true)}
              >
                Calculate Closing Costs
              </Button>
            </CardContent>
          </Card>

          {/* Features Card */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Features
              </Typography>
              
              {/* Categorized Lists */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {property.features.heating && property.features.heating.length > 0 && (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">Heating</Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.5 }}>
                      {property.features.heating.map((item, i) => (
                        <Chip key={i} label={item} size="small" variant="outlined" />
                      ))}
                    </Box>
                  </Box>
                )}

                {property.features.cooling && property.features.cooling.length > 0 && (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">Cooling</Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.5 }}>
                      {property.features.cooling.map((item, i) => (
                        <Chip key={i} label={item} size="small" variant="outlined" />
                      ))}
                    </Box>
                  </Box>
                )}

                {property.features.flooring && property.features.flooring.length > 0 && (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">Flooring</Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.5 }}>
                      {property.features.flooring.map((item, i) => (
                        <Chip key={i} label={item} size="small" variant="outlined" />
                      ))}
                    </Box>
                  </Box>
                )}

                {property.features.appliances && property.features.appliances.length > 0 && (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">Appliances</Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.5 }}>
                      {property.features.appliances.map((item, i) => (
                        <Chip key={i} label={item} size="small" variant="outlined" />
                      ))}
                    </Box>
                  </Box>
                )}
                
                {property.features.security && property.features.security.length > 0 && (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">Security</Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.5 }}>
                      {property.features.security.map((item, i) => (
                        <Chip key={i} label={item} size="small" variant="outlined" />
                      ))}
                    </Box>
                  </Box>
                )}

                 {property.features.outdoor && property.features.outdoor.length > 0 && (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">Outdoor</Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.5 }}>
                      {property.features.outdoor.map((item, i) => (
                        <Chip key={i} label={item} size="small" variant="outlined" />
                      ))}
                    </Box>
                  </Box>
                )}

                {property.features.accessibility && property.features.accessibility.length > 0 && (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">Accessibility</Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.5 }}>
                      {property.features.accessibility.map((item, i) => (
                        <Chip key={i} label={item} size="small" variant="outlined" />
                      ))}
                    </Box>
                  </Box>
                )}

                {/* Boolean Features */}
                <Box>
                  {(property.features.heating?.length > 0 || property.features.cooling?.length > 0 || property.features.flooring?.length > 0 || property.features.appliances?.length > 0) && (
                     <Typography variant="subtitle2" color="text.secondary" gutterBottom>Additional Features</Typography>
                  )}
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {property.features.pool && (
                      <Chip icon={<Pool />} label="Pool" size="small" />
                    )}
                    {property.features.garage && (
                      <Chip icon={<Garage />} label="Garage" size="small" />
                    )}
                    {property.features.garden && (
                      <Chip label="Garden" size="small" />
                    )}
                    {property.features.fireplace && (
                      <Chip icon={<Fireplace />} label="Fireplace" size="small" />
                    )}
                    {property.features.balcony && (
                      <Chip icon={<Balcony />} label="Balcony" size="small" />
                    )}
                    {property.features.basement && (
                      <Chip label="Basement" size="small" />
                    )}
                    {property.features.attic && (
                      <Chip label="Attic" size="small" />
                    )}
                    {property.features.energyEfficient && (
                      <Chip label="Energy Efficient" size="small" />
                    )}
                    {property.features.smartHome && (
                      <Chip label="Smart Home" size="small" />
                    )}
                    {property.metadata.petFriendly && (
                      <Chip label="Pet Friendly" size="small" />
                    )}
                    {property.metadata.furnished && (
                      <Chip label="Furnished" size="small" />
                    )}
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Immersive View Card */}
          <Card sx={{ mb: 3, border: '1px solid rgba(197, 160, 89, 0.3)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <ViewInAr color="primary" />
                <Typography variant="h6" sx={{ fontFamily: '"Playfair Display", serif', fontWeight: 600 }}>
                  Immersive Experience
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" paragraph>
                Explore this unit exactly as it will be built.
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Button 
                  variant="outlined" 
                  fullWidth 
                  startIcon={<ViewInAr />}
                  sx={{ py: 1.5, justifyContent: 'flex-start' }}
                >
                  Launch 360° Virtual Tour
                </Button>
                <Button 
                  variant="outlined" 
                  fullWidth 
                  startIcon={<Visibility />}
                  sx={{ py: 1.5, justifyContent: 'flex-start' }}
                >
                  View from Balcony (Level 5)
                </Button>
              </Box>
            </CardContent>
          </Card>

          {/* Analytics Card */}
          {property.analytics && (
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Statistics
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Views: {viewCount}
                </Typography>
                {property.analytics.favorites > 0 && (
                  <Typography variant="body2" color="text.secondary">
                    Favorites: {property.analytics.favorites}
                  </Typography>
                )}
                {property.analytics.inquiries > 0 && (
                  <Typography variant="body2" color="text.secondary">
                    Inquiries: {property.analytics.inquiries}
                  </Typography>
                )}
              </CardContent>
            </Card>
          )}
        </Grid>

        {/* Description */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Description
            </Typography>
            <Typography variant="body1" paragraph>
              {property.metadata.description}
            </Typography>

            {property.metadata.amenities && property.metadata.amenities.length > 0 && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Amenities
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {property.metadata.amenities.map((amenity, index) => (
                    <Chip key={index} label={amenity} size="small" />
                  ))}
                </Box>
              </Box>
            )}

            {property.tags && property.tags.length > 0 && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Tags
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {property.tags.map((tag, index) => (
                    <Chip key={index} label={tag} size="small" variant="outlined" />
                  ))}
                </Box>
              </Box>
            )}

            <Box sx={{ mt: 3, display: 'flex', gap: 2, color: 'text.secondary' }}>
              <Typography variant="body2">
                Listed: {format((property.listingDate as any).toDate(), 'MMM dd, yyyy')}
              </Typography>
              {property.expirationDate && (
                <Typography variant="body2">
                  Expires: {format((property.expirationDate as any).toDate(), 'MMM dd, yyyy')}
                </Typography>
              )}
            </Box>

            {/* Media & Documents */}
            {(property.metadata.virtualTour || property.metadata.videoUrl || (property.metadata.documents && property.metadata.documents.length > 0)) && (
              <Box sx={{ mt: 4 }}>
                <Typography variant="h6" gutterBottom>
                  Media & Documents
                </Typography>
                <Grid container spacing={2}>
                   {property.metadata.virtualTour && (
                     <Grid item xs={12} sm={6}>
                       <Button 
                          variant="outlined" 
                          fullWidth 
                          href={property.metadata.virtualTour}
                          target="_blank"
                          rel="noopener noreferrer"
                          startIcon={<ThreeDRotation />}
                       >
                          View Virtual Tour
                       </Button>
                     </Grid>
                   )}
                   {property.metadata.videoUrl && (
                     <Grid item xs={12} sm={6}>
                        <Button 
                          variant="outlined" 
                          fullWidth 
                          href={property.metadata.videoUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          startIcon={<PlayCircle />}
                       >
                          Watch Video
                       </Button>
                     </Grid>
                   )}
                </Grid>
                
                {property.metadata.documents && property.metadata.documents.length > 0 && (
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="subtitle1" gutterBottom>
                       Documents
                    </Typography>
                    <List dense>
                       {property.metadata.documents.map((doc, index) => {
                           let displayName = `Document ${index + 1}`;
                           try {
                               const decoded = decodeURIComponent(doc);
                               const parts = decoded.split('/');
                               const fileName = parts[parts.length - 1].split('?')[0];
                               // Try to make it readable if it follows the pattern
                               if (fileName.startsWith('property-')) {
                                   // property-{id}-doc-{timestamp}
                                   // Just show the end part if possible, or keep it as is
                                   displayName = fileName;
                               } else {
                                   displayName = fileName;
                               }
                           } catch (e) {}
                           
                           return (
                               <ListItem key={index} disablePadding sx={{ mb: 1 }}>
                                   <ListItemIcon sx={{ minWidth: 40 }}>
                                       <Description color="action" /> 
                                   </ListItemIcon>
                                   <ListItemText 
                                      primary={
                                          <Link href={doc} target="_blank" rel="noopener noreferrer" underline="hover" color="primary">
                                              {displayName}
                                          </Link>
                                      } 
                                   />
                               </ListItem>
                           )
                       })}
                    </List>
                  </Box>
                )}
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Lightbox Dialog */}
      <Dialog
        open={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: 'rgba(0,0,0,0.9)',
            color: 'white',
          },
        }}
      >
        <DialogContent sx={{ p: 0, position: 'relative' }}>
          <IconButton
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              zIndex: 1,
              color: 'white',
            }}
            onClick={() => setLightboxOpen(false)}
          >
            <CloseIcon />
          </IconButton>
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              height: '80vh',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <img
              src={images[selectedImageIndex]}
              alt={`${property.metadata.title} - Image ${selectedImageIndex + 1}`}
              style={{
                maxWidth: '100%',
                maxHeight: '100%',
                objectFit: 'contain',
              }}
            />
            {images.length > 1 && (
              <>
                <IconButton
                  sx={{
                  position: 'absolute',
                  left: 16,
                  color: 'white',
                  bgcolor: 'rgba(0,0,0,0.5)',
                  }}
                  onClick={handlePrevImage}
                >
                  <NavigateBefore />
                </IconButton>
                <IconButton
                  sx={{
                  position: 'absolute',
                  right: 16,
                  color: 'white',
                  bgcolor: 'rgba(0,0,0,0.5)',
                  }}
                  onClick={handleNextImage}
                >
                  <NavigateNext />
                </IconButton>
              </>
            )}
          </Box>
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="white">
              {selectedImageIndex + 1} / {images.length}
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Cost Calculator Dialog */}
      <Dialog
        open={calculatorOpen}
        onClose={() => setCalculatorOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2, p: 2 }
        }}
      >
        <DialogContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5" sx={{ fontFamily: '"Playfair Display", serif', fontWeight: 600 }}>
              Transparent Cost Calculator
            </Typography>
            <IconButton onClick={() => setCalculatorOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
          
          <Typography variant="body2" color="text.secondary" paragraph>
            At souk.immo, we believe in zero hidden fees. Here is an exact breakdown of the acquisition costs for this property based on Moroccan standards.
          </Typography>

          <List sx={{ width: '100%', bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
            <ListItem sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
              <ListItemText primary="Property Price" />
              <Typography fontWeight="bold">{formatCurrency(property.metadata.price)}</Typography>
            </ListItem>
            <ListItem sx={{ borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'rgba(0,0,0,0.02)' }}>
              <ListItemText primary="Registration Duty (4%)" secondary="Droits d'Enregistrement" />
              <Typography color="text.secondary">{formatCurrency(registrationDuty)}</Typography>
            </ListItem>
            <ListItem sx={{ borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'rgba(0,0,0,0.02)' }}>
              <ListItemText primary="Land Registry (1.5%)" secondary="Conservation Foncière" />
              <Typography color="text.secondary">{formatCurrency(landRegistryFee)}</Typography>
            </ListItem>
            <ListItem sx={{ borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'rgba(0,0,0,0.02)' }}>
              <ListItemText primary="Notary Honoraria (~1%)" secondary="Frais de Notaire" />
              <Typography color="text.secondary">{formatCurrency(notaryFee)}</Typography>
            </ListItem>
            <ListItem sx={{ bgcolor: 'primary.main', color: 'primary.contrastText', borderBottomLeftRadius: 4, borderBottomRightRadius: 4 }}>
              <ListItemText 
                primary={<Typography fontWeight="bold">Total Estimated Cost</Typography>} 
                secondary={<Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>Including all taxes and fees</Typography>}
              />
              <Typography variant="h6" fontWeight="bold">{formatCurrency(totalCost)}</Typography>
            </ListItem>
          </List>
          
          <Button 
            variant="contained" 
            fullWidth 
            size="large" 
            sx={{ mt: 3, py: 1.5 }}
            onClick={() => setCalculatorOpen(false)}
          >
            I Understand
          </Button>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default PropertyDetail;
